import React, { useState, useEffect } from "react";
import MappingTopBox from "./CreatorTools/MappingTool/components/MappingTopBox";
import { doc, getDoc, setDoc, collection, query, where, getDocs } from "firebase/firestore";
import { useFirestore } from "reactfire";
import { toast } from "react-toastify";
import { toStorageNoteType, fromStorageNoteType } from "./CreatorTools/MappingTool/utils/NoteType";
import { NOTE_TYPE_VALUES } from "@rebuild/shared";
import { NoteType } from "./CreatorTools/MappingTool/utils/NoteType";
import { FilterableSelect } from "./components/GenericInputComponents";
import { useOrganizationSelect } from "../hooks/useOrganizationSelect";
import OrganizationSelector from "../components/ui/utils/OrganizationSelector";

const CustomReesePromptsPage: React.FC = () => {
	const { organizations, selectedOrganization, setSelectedOrganization } = useOrganizationSelect({ includeTestOrgs: true });

	// Use note type, not estimate type because we need to be able to differentiate between Interior and Exterior for prompts.
	// Stored lowercase in Firestore for backward compatibility
	const [selectedNoteType, setSelectedNoteType] = useState<NoteType>(NoteType.MITIGATION);
	const [firstMessage, setFirstMessage] = useState<string>("");
	const [personality, setPersonality] = useState<string>("");
	const [goal, setGoal] = useState<string>("");
	const [guardrails, setGuardrails] = useState<string>("");
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isSaving, setIsSaving] = useState<boolean>(false);
	const [documentId, setDocumentId] = useState<string | null>(null);

	const firestore = useFirestore();

	// Load the prompts from Firestore when customer or estimate type changes
	useEffect(() => {
		const loadPrompts = async () => {
			setIsLoading(true);
			try {
				const promptsCollection = collection(firestore, "custom_system_prompts");
				const q = query(
					promptsCollection,
					where("organizationId", "==", selectedOrganization.organizationId),
					where("noteType", "==", toStorageNoteType(selectedNoteType)),
				);

				const querySnapshot = await getDocs(q);

				if (!querySnapshot.empty) {
					const docData = querySnapshot.docs[0];
					const data = docData.data();
					setDocumentId(docData.id);
					setFirstMessage(data.firstMessage || "");
					setPersonality(data.personality || "");
					setGoal(data.goal || "");
					setGuardrails(data.guardrails || "");
					// Map stored lowercase to NoteType for select display
					if (data.noteType) {
						setSelectedNoteType(fromStorageNoteType(data.noteType));
					}
				} else {
					setDocumentId(null);
					setFirstMessage("");
					setPersonality("");
					setGoal("");
					setGuardrails("");
				}
			} catch (error) {
				console.error("Error loading prompts:", error);
				toast.error("Failed to load prompts");
			} finally {
				setIsLoading(false);
			}
		};

		loadPrompts();
	}, [selectedOrganization, selectedNoteType, firestore]);

	// Save the prompts to Firestore
	const handleSave = async () => {
		setIsSaving(true);
		try {
			const promptsCollection = collection(firestore, "custom_system_prompts");

			// If we have an existing document, update it; otherwise create a new one
			if (documentId) {
				const docRef = doc(promptsCollection, documentId);
				await setDoc(
					docRef,
					{
						organizationId: selectedOrganization.organizationId,
						noteType: toStorageNoteType(selectedNoteType),
						firstMessage: firstMessage,
						personality: personality,
						goal: goal,
						guardrails: guardrails,
					},
					{ merge: true },
				);
			} else {
				// Create a new document with auto-generated ID
				const newDocRef = doc(promptsCollection);
				await setDoc(newDocRef, {
					organizationId: selectedOrganization.organizationId,
					noteType: toStorageNoteType(selectedNoteType),
					firstMessage: firstMessage,
					personality: personality,
					goal: goal,
					guardrails: guardrails,
				});
				setDocumentId(newDocRef.id);
			}

			toast.success("Prompts saved successfully!");
		} catch (error) {
			console.error("Error saving prompts:", error);
			toast.error("Failed to save prompts");
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<div className="min-h-screen bg-gray-50">
			<MappingTopBox />
			<div className="ml-8 m4">
				<div className="mt-4 mb-8">
					<h1 className="text-3xl font-bold text-gray-800 mb-6">Custom Reese Prompts</h1>
					<p className="text-gray-600 mb-2">* This page is used to configure custom Reese prompts for customers and estimate types. </p>
					<p className="text-gray-600 mb-2">
						* Personality, Goal, and Guardrails are 3 different parts of the prompt. Clicking "Save" up top will save all 3
					</p>
					<p className="text-gray-600 mb-6">
						* If a custom prompt is not defined for a customer-estimate type pairing, the default prompt will be used.
					</p>
					<div className="flex items-end gap-8 mb-8">
						<div className="flex-1">
							<OrganizationSelector selectedOrganization={selectedOrganization} organizations={organizations} onSelect={setSelectedOrganization} />
						</div>

						<div className="flex-1">
							<label className="block text-sm font-medium text-gray-700 mb-2">Estimate Type</label>
							<FilterableSelect
								value={selectedNoteType}
								onSelect={(newNoteType) => {
									setSelectedNoteType((newNoteType as NoteType) ?? NoteType.MITIGATION);
								}}
								options={NOTE_TYPE_VALUES}
							/>
						</div>

						<button
							onClick={handleSave}
							disabled={isSaving}
							className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
						>
							{isSaving ? (
								<>
									<svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
										<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
										<path
											className="opacity-75"
											fill="currentColor"
											d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
										></path>
									</svg>
									Saving...
								</>
							) : (
								<>
									<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
									</svg>
									Save
								</>
							)}
						</button>
					</div>
				</div>

				<div className="space-y-8">
					{isLoading ? (
						<div className="bg-white p-6 rounded-lg shadow">
							<div className="flex items-center justify-center py-12">
								<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
								<span className="ml-3 text-gray-600">Loading prompts...</span>
							</div>
						</div>
					) : (
						<>
							{/* First Question Section */}
							<div className="bg-white p-6 rounded-lg shadow">
								<h2 className="text-xl font-semibold text-gray-800 mb-4">First Message</h2>
								<p className="text-sm text-gray-500">Optional. If set, Reese will send this as the opening message instead of the default.</p>
								<p className="text-sm text-gray-500">In v1, the default message is "Hey there, I'm Reese. What's the address of the property you're working on?".</p>
								<p className="text-sm text-gray-500">In v2, the default message is "Hey there, I'm Reese. What room would you like to start with?".</p>
								<p className="text-sm text-gray-500 mb-3">This usually comprises of an introduction and a question.</p>
								<input
									type="text"
									value={firstMessage}
									onChange={(e) => setFirstMessage(e.target.value)}
									className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
									placeholder="Enter the first message Reese should send..."
								/>
							</div>

							{/* Personality Section */}
							<div className="bg-white p-6 rounded-lg shadow">
								<h2 className="text-xl font-semibold text-gray-800 mb-4">Personality</h2>
								<textarea
									value={personality}
									onChange={(e) => setPersonality(e.target.value)}
									className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
									rows={3}
									placeholder="Enter personality instructions here..."
								/>
							</div>

							{/* Goal Section */}
							<div className="bg-white p-6 rounded-lg shadow">
								<h2 className="text-xl font-semibold text-gray-800 mb-4">Goal</h2>
								<textarea
									value={goal}
									onChange={(e) => setGoal(e.target.value)}
									className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
									rows={15}
									placeholder="Enter goal instructions here..."
								/>
							</div>

							{/* Guardrails Section */}
							<div className="bg-white p-6 rounded-lg shadow">
								<h2 className="text-xl font-semibold text-gray-800 mb-4">Guardrails</h2>
								<textarea
									value={guardrails}
									onChange={(e) => setGuardrails(e.target.value)}
									className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
									rows={8}
									placeholder="Enter guardrails instructions here..."
								/>
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
};

export default CustomReesePromptsPage;
